import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetProductCategoriesWithProducts, useParamQuery } from 'src/hooks';
import { formatQuery, lang } from 'src/utils';

import {
  Column,
  PRODUCT_CATEGORY_LIST_COLUMN,
} from '../ManageProductCategoryPage.interface';
import { getTotalLengthPerPage } from '../modules/ManageProductCategory.helper';

export const useManageProductCategoryPage = () => {
  const location = useLocation();

  const {
    data: productCategoriesWithProducts,
    isLoading,
    hasNextPage,
    fetchNextPage,
    refetch: refetchProductCategoriesWithProducts,
  } = useGetProductCategoriesWithProducts(formatQuery(location.search));

  useEffect(() => {
    refetchProductCategoriesWithProducts();
  }, [location.search, refetchProductCategoriesWithProducts]);

  const totalCount = productCategoriesWithProducts?.pages?.[0].totalCount || 0;
  const numberofResults = getTotalLengthPerPage(
    productCategoriesWithProducts?.pages ?? [],
  );
  const [activeColumn, setActiveColumn] = useState<string>(
    PRODUCT_CATEGORY_LIST_COLUMN.NAME,
  );

  const [sorting, setSorting] = useState({
    columnKey: PRODUCT_CATEGORY_LIST_COLUMN.NAME,
    order: 'asc',
  });

  const { activeFilters, onChangeParams } = useParamQuery({
    sort: [PRODUCT_CATEGORY_LIST_COLUMN.NAME],
  });

  const getNextProductCategoriesWithProductsPage = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const handleSort = (
    columnKey: PRODUCT_CATEGORY_LIST_COLUMN,
    order: string,
  ) => {
    setSorting(() => ({
      columnKey,
      order,
    }));
    setActiveColumn(columnKey);
    onChangeParams('sort', [
      `${order === 'asc' ? columnKey : `-${columnKey}`}`,
    ]);
  };

  const columns: Column[] = [
    {
      label: lang.productCategory,
      key: PRODUCT_CATEGORY_LIST_COLUMN.NAME,
      sortIcon: true,
    },
    {
      label: lang.numberOfProducts,
      key: PRODUCT_CATEGORY_LIST_COLUMN.PRODUCTS_COUNT,
      sortIcon: true,
    },
    { label: '', key: PRODUCT_CATEGORY_LIST_COLUMN.ACTIONS, sortIcon: false },
  ];

  return {
    productCategoriesWithProducts,
    isLoading,
    hasActiveFilters: !isEmpty(activeFilters),
    getNextProductCategoriesWithProductsPage,
    onChangeParams,
    columns,
    handleSort,
    activeColumn,
    sorting,
    hasNextPage,
    totalCount,
    numberofResults,
    activeFilters,
  };
};
